import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { apiError, fileUrl, inr } from "../../lib/api";
import { PageHeader, Card, Loading, inputCls, labelCls, btnPrimary, btnSecondary } from "../../components/admin/common";
import { Save, Rocket, Upload, X, Plus, Loader2, ImageIcon, QrCode, Copy, Mail } from "lucide-react";
import { toast } from "sonner";
import FormFieldsBuilder from "../../components/admin/FormFieldsBuilder";
import WhatsAppConfig from "../../components/admin/WhatsAppConfig";
import RichTextEditor from "../../components/admin/RichTextEditor";
import { PageLanguageBar } from "../../components/admin/PageLanguage";
import { DEFAULT_FIELDS } from "../../lib/formFields";

const DEFAULT_REFUND_POLICY = "We understand that unexpected situations may arise, and we strive to keep our policy fair and transparent. If, due to an unforeseen reason, your child is unable to attend the workshop, kindly inform us at least one day before the workshop begins.";

export default function PageBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = !!id;
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [schools, setSchools] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [levels, setLevels] = useState([]);
  const [batches, setBatches] = useState([]);
  const coverRef = useRef(); const qrRef = useRef();
  const [uploading, setUploading] = useState("");

  const [f, setF] = useState({
    title: "", subtitle: "", cover_image: "", description: "", school_id: "", program_id: "",
    level_id: "", batch_id: "", fee: 0, discount: 0, tax: 0, currency: "INR", payment_description: "",
    upi_id: "", payee_name: "", qr_image: "", whatsapp_link: "", status: "draft", reg_prefix: "", seat_limit: null, form_fields: DEFAULT_FIELDS,
    enabled_upi_apps: ["gpay", "phonepe", "paytm", "bhim", "other"], refund_policy: DEFAULT_REFUND_POLICY,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [lang, setLang] = useState("en");
  const tv = (k) => (lang === "en" ? f[k] : f.translations?.[lang]?.[k]) || "";
  const ts = (k, v) => (lang === "en" ? set(k, v) : setF((p) => ({ ...p, translations: { ...(p.translations || {}), [lang]: { ...(p.translations?.[lang] || {}), [k]: v } } })));
  const ph = (k) => (lang === "en" ? undefined : String(f[k] || "").replace(/<[^>]*>/g, " ").trim().slice(0, 80) || undefined);

  const loadMasters = async () => {
    const [s, p] = await Promise.all([api.get("/schools"), api.get("/programs")]);
    setSchools(s.data); setPrograms(p.data);
  };
  useEffect(() => {
    loadMasters();
    if (editing) api.get(`/pages/${id}`).then((r) => { const d = r.data; setF((p) => ({ ...p, ...d, form_fields: d.form_fields && d.form_fields.length ? d.form_fields : DEFAULT_FIELDS, refund_policy: (d.refund_policy || "").trim() ? d.refund_policy : DEFAULT_REFUND_POLICY })); setLoading(false); }).catch(() => setLoading(false));
  }, [id]);

  useEffect(() => { if (f.program_id) api.get(`/levels?program_id=${f.program_id}`).then((r) => setLevels(r.data)); else setLevels([]); }, [f.program_id]);
  useEffect(() => { if (f.level_id) api.get(`/batches?level_id=${f.level_id}`).then((r) => setBatches(r.data)); else setBatches([]); }, [f.level_id]);

  const quickAdd = async (endpoint, extra = {}) => {
    const name = window.prompt(`New ${endpoint.replace(/s$/, "")} name:`);
    if (!name) return;
    try {
      const { data } = await api.post(`/${endpoint}`, { name, ...extra });
      toast.success("Added");
      if (endpoint === "schools") { await loadMasters(); set("school_id", data.id); }
      if (endpoint === "programs") { await loadMasters(); set("program_id", data.id); }
      if (endpoint === "levels") { const r = await api.get(`/levels?program_id=${f.program_id}`); setLevels(r.data); set("level_id", data.id); }
      if (endpoint === "batches") { const r = await api.get(`/batches?level_id=${f.level_id}`); setBatches(r.data); set("batch_id", data.id); }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const upload = async (file, field) => {
    if (!file) return;
    setUploading(field);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/asset", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(field, data.path);
      toast.success("Image uploaded");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setUploading("");
  };

  const save = async (status) => {
    if (!f.title) { toast.error("Page title is required"); return; }
    setSaving(true);
    const payload = { ...f, status: status || f.status, fee: Number(f.fee) || 0, discount: Number(f.discount) || 0, tax: Number(f.tax) || 0 };
    try {
      let res;
      if (editing) res = await api.put(`/pages/${id}`, payload);
      else res = await api.post("/pages", payload);
      toast.success(status === "published" ? "Page published!" : "Page saved");
      navigate("/admin/pages");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  if (loading) return <Loading />;
  const finalFee = Math.max(0, (Number(f.fee) || 0) - (Number(f.discount) || 0) + (Number(f.tax) || 0));

  return (
    <div>
      <PageHeader title={editing ? "Edit Page" : "Create New Page"} subtitle="Configure a GDIOT PAY registration & payment page.">
        <button className={btnSecondary} onClick={() => save(f.status)} disabled={saving} data-testid="save-draft-btn"><Save className="w-4 h-4" /> Save</button>
        <button className={btnPrimary} onClick={() => save("published")} disabled={saving} data-testid="publish-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Publish</button>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 space-y-4">
            <h3 className="font-head font-semibold text-slate-900">Basic Information</h3>
            {editing && f.slug && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3" data-testid="page-permanent-link-box">
                <div className="text-[10px] font-semibold text-blue-500 uppercase tracking-wider mb-1">Page Link — Permanent</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm text-blue-800 font-mono truncate" data-testid="page-permanent-link">{`${window.location.origin}/r/${f.slug}`}</code>
                  <button type="button" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/r/${f.slug}`); toast.success("Link copied"); }} className="w-8 h-8 rounded-lg hover:bg-blue-100 text-blue-600 flex items-center justify-center" title="Copy link" data-testid="copy-page-link"><Copy className="w-4 h-4" /></button>
                </div>
                <p className="text-[11px] text-blue-500 mt-1">This link never changes — edit the title, fields, fee or anything else and it stays exactly the same.</p>
              </div>
            )}
            <PageLanguageBar lang={lang} setLang={setLang} f={f} setTranslations={(t) => set("translations", t)} />
            <div><label className={labelCls}>Page Title *{lang !== "en" && ` (${lang === "hi" ? "Hindi" : "Marathi"})`}</label><input className={inputCls} value={tv("title")} onChange={(e) => ts("title", e.target.value)} placeholder={ph("title") || "GDIOT Advanced Drone STEM Program"} data-testid="page-title" /></div>
            <div><label className={labelCls}>Subtitle</label><input className={inputCls} value={tv("subtitle")} onChange={(e) => ts("subtitle", e.target.value)} placeholder={ph("subtitle") || "Hands-on drone building & coding"} data-testid="page-subtitle" /></div>
            <div><label className={labelCls}>Registration ID Prefix</label><input className={inputCls} value={f.reg_prefix} onChange={(e) => set("reg_prefix", e.target.value)} placeholder="e.g. GDIOT-AI-1MONTH-26" data-testid="page-reg-prefix" /><p className="text-xs text-slate-400 mt-1">Registration IDs for this program become PREFIX-001, PREFIX-002… Leave blank to use the default GDIOT-2026 numbering. Saving with a prefix renumbers this program's existing registrations.</p></div>
            <div>
              <label className={labelCls}>Cover Image</label>
              {f.cover_image ? (
                <div className="relative w-full rounded-xl overflow-hidden border border-slate-200 bg-white">
                  <img src={fileUrl(f.cover_image)} alt="cover" className="w-full h-auto object-contain" />
                  <button onClick={() => set("cover_image", "")} className="absolute top-2 right-2 w-8 h-8 rounded-lg bg-black/60 text-white flex items-center justify-center"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <button onClick={() => coverRef.current.click()} className="w-full h-32 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center text-slate-400 hover:border-blue-400 hover:text-blue-500 transition-colors" data-testid="upload-cover">
                  {uploading === "cover_image" ? <Loader2 className="w-6 h-6 animate-spin" /> : <><ImageIcon className="w-6 h-6 mb-1" /><span className="text-sm">Upload cover image</span></>}
                </button>
              )}
              <input ref={coverRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files[0], "cover_image")} />
            </div>
            <div><label className={labelCls}>Program Description</label><RichTextEditor key={`desc-${lang}`} value={tv("description")} onChange={(html) => ts("description", html)} placeholder={ph("description") || "Describe the program, eligibility, duration, what students will learn…"} testid="page-description" /></div>
            <div>
              <label className={labelCls}>Refund Policy</label>
              <textarea className={inputCls} rows={3} value={tv("refund_policy")} onChange={(e) => ts("refund_policy", e.target.value)} placeholder={ph("refund_policy") || "Refund / cancellation policy shown to customers on the payment pages…"} data-testid="page-refund-policy" />
              <p className="text-[11px] text-slate-400 mt-1">Shown below the contact block on the registration, payment and confirmation pages. Edit it per form.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
              <div>
                <label className={labelCls}>Support call number</label>
                <input className={inputCls} value={f.support_phone || ""} onChange={(e) => set("support_phone", e.target.value)} placeholder="Uses Settings number if empty" data-testid="page-support-phone" />
              </div>
              <div>
                <label className={labelCls}>Screenshot help WhatsApp</label>
                <input className={inputCls} value={f.support_whatsapp || ""} onChange={(e) => set("support_whatsapp", e.target.value)} placeholder="Default 9021824051" data-testid="page-support-whatsapp" />
              </div>
              <p className="sm:col-span-2 text-[11px] text-slate-400">Shown at the bottom of this link only ("For any technical help, contact us" and "Not able to upload the screenshot?"). Leave empty to use the default.</p>
            </div>
          </Card>

          <Card className="p-6 space-y-4">
            <h3 className="font-head font-semibold text-slate-900">Classification</h3>
            <SelectWithAdd label="School" value={f.school_id} onChange={(v) => set("school_id", v)} options={schools} onAdd={() => quickAdd("schools")} testid="select-school" />
            <SelectWithAdd label="Program" value={f.program_id} onChange={(v) => { set("program_id", v); set("level_id", ""); set("batch_id", ""); }} options={programs} onAdd={() => quickAdd("programs")} testid="select-program" />
            <SelectWithAdd label="Level" value={f.level_id} onChange={(v) => { set("level_id", v); set("batch_id", ""); }} options={levels} onAdd={() => f.program_id ? quickAdd("levels", { program_id: f.program_id }) : toast.error("Select a program first")} testid="select-level" />
            <SelectWithAdd label="Batch" value={f.batch_id} onChange={(v) => set("batch_id", v)} options={batches} onAdd={() => f.level_id ? quickAdd("batches", { level_id: f.level_id }) : toast.error("Select a level first")} testid="select-batch" />
          </Card>

          <Card className="p-6 space-y-4">
            <div>
              <h3 className="font-head font-semibold text-slate-900">Registration Form Fields</h3>
              <p className="text-slate-500 text-sm">Pick exactly which fields the parent form collects. Add or remove blocks, mark them required, reorder, or create custom fields.</p>
            </div>
            <FormFieldsBuilder value={f.form_fields} onChange={(ff) => set("form_fields", ff)} />
          </Card>

          <Card className="p-6 space-y-4">
            <h3 className="font-head font-semibold text-slate-900">Payment Configuration</h3>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelCls}>UPI ID</label><input className={inputCls} value={f.upi_id} onChange={(e) => set("upi_id", e.target.value)} placeholder="gdiot@upi" data-testid="page-upi" /></div>
              <div><label className={labelCls}>Payee Name</label><input className={inputCls} value={f.payee_name} onChange={(e) => set("payee_name", e.target.value)} placeholder="GDIOT Innovations LLP" /></div>
            </div>
            <div><label className={labelCls}>Payment Description{lang !== "en" && ` (${lang === "hi" ? "Hindi" : "Marathi"})`}</label><input className={inputCls} value={tv("payment_description")} onChange={(e) => ts("payment_description", e.target.value)} placeholder={ph("payment_description") || "Level 1 Drone STEM Registration Fee"} data-testid="page-payment-description" /></div>
            <div>
              <label className={labelCls}>Payment QR Code (upload)</label>
              {f.qr_image ? (
                <div className="relative w-40 h-40 rounded-xl overflow-hidden border border-slate-200">
                  <img src={fileUrl(f.qr_image)} alt="qr" className="w-full h-full object-contain bg-white" />
                  <button onClick={() => set("qr_image", "")} className="absolute top-1 right-1 w-7 h-7 rounded-lg bg-black/60 text-white flex items-center justify-center"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <button onClick={() => qrRef.current.click()} className="w-40 h-40 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center text-slate-400 hover:border-blue-400 hover:text-blue-500" data-testid="upload-qr">
                  {uploading === "qr_image" ? <Loader2 className="w-6 h-6 animate-spin" /> : <><QrCode className="w-6 h-6 mb-1" /><span className="text-xs">Upload QR</span></>}
                </button>
              )}
              <input ref={qrRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files[0], "qr_image")} />
            </div>
            <div><label className={labelCls}>Seat Limit (auto "Batch Full")</label>
              <input type="number" min={0} className={inputCls} value={f.seat_limit ?? ""} placeholder="No limit"
                onChange={(e) => set("seat_limit", e.target.value === "" ? null : Math.max(0, parseInt(e.target.value, 10) || 0))} data-testid="page-seat-limit" />
              <p className="text-[11px] text-slate-400 mt-1">When this many registrations are fully paid{f.completed_count != null ? ` (currently ${f.completed_count})` : ""}, the page closes automatically and new parents see "This Batch is Currently Full". Leave empty for no limit.</p></div>
            <div><label className={labelCls}>WhatsApp Group Link</label><input className={inputCls} value={f.whatsapp_link} onChange={(e) => set("whatsapp_link", e.target.value)} placeholder="https://chat.whatsapp.com/…" data-testid="page-whatsapp" /></div>
            <div>
              <label className={labelCls}>Available UPI Apps (shown on payment page)</label>
              <div className="flex flex-wrap gap-2">
                {UPI_APP_OPTIONS.map((a) => {
                  const on = (f.enabled_upi_apps || []).includes(a.key);
                  return (
                    <button type="button" key={a.key} data-testid={`upi-toggle-${a.key}`}
                      onClick={() => set("enabled_upi_apps", on ? (f.enabled_upi_apps || []).filter((k) => k !== a.key) : [...(f.enabled_upi_apps || []), a.key])}
                      className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${on ? "bg-blue-50 border-blue-300 text-blue-700" : "bg-slate-50 border-slate-200 text-slate-500"}`}>
                      {on ? "✓ " : ""}{a.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-head font-semibold text-slate-900 flex items-center gap-2"><Mail className="w-4 h-4 text-blue-600" /> Email Confirmation After Payment</h3>
                <p className="text-xs text-slate-500 mt-1">Automatically email the parent a payment confirmation (with the PDF invoice attached) once the payment is completed.</p>
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <span className="text-sm text-slate-600">{(f.email_config?.enabled !== false) ? "ON" : "OFF"}</span>
                <button type="button" data-testid="email-enable-toggle"
                  onClick={() => set("email_config", { ...(f.email_config || {}), enabled: !(f.email_config?.enabled !== false) })}
                  className={`relative w-11 h-6 rounded-full transition-colors ${(f.email_config?.enabled !== false) ? "bg-blue-600" : "bg-slate-300"}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${(f.email_config?.enabled !== false) ? "translate-x-5" : ""}`} />
                </button>
              </label>
            </div>
          </Card>

          <Card className="p-6">
            <WhatsAppConfig value={f.whatsapp_config} onChange={(cfg) => set("whatsapp_config", cfg)} />
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="p-6 space-y-4 sticky top-20">
            <h3 className="font-head font-semibold text-slate-900">Fee</h3>
            <div><label className={labelCls}>Base Fee (₹)</label><input type="number" className={inputCls} value={f.fee} onChange={(e) => set("fee", e.target.value)} data-testid="page-fee" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Discount (₹)</label><input type="number" className={inputCls} value={f.discount} onChange={(e) => set("discount", e.target.value)} /></div>
              <div><label className={labelCls}>Tax (₹)</label><input type="number" className={inputCls} value={f.tax} onChange={(e) => set("tax", e.target.value)} /></div>
            </div>
            <div className="border-t border-slate-100 pt-4 flex items-center justify-between">
              <span className="text-sm font-medium text-slate-600">Amount Payable</span>
              <span className="font-head text-2xl font-extrabold text-blue-600">{inr(finalFee)}</span>
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <select className={inputCls} value={f.status} onChange={(e) => set("status", e.target.value)} data-testid="page-status">
                {["draft", "published", "paused", "closed", "archived"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

const UPI_APP_OPTIONS = [
  { key: "gpay", label: "Google Pay" },
  { key: "phonepe", label: "PhonePe" },
  { key: "paytm", label: "Paytm" },
  { key: "bhim", label: "BHIM" },
  { key: "other", label: "Other UPI Apps" },
];

function SelectWithAdd({ label, value, onChange, options, onAdd, testid }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="flex gap-2">
        <select className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
          <option value="">Select {label}…</option>
          {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <button onClick={onAdd} className="flex-shrink-0 w-11 rounded-lg border border-slate-200 bg-slate-50 hover:bg-blue-50 hover:text-blue-600 text-slate-500 flex items-center justify-center" title={`Add ${label}`} data-testid={`add-${testid}`}><Plus className="w-4 h-4" /></button>
      </div>
    </div>
  );
}
